export const PENDING_ACTIVATION_KEY = 'workplansiq.pendingActivation';
export const ACTIVATION_CONTINUATION_KEY = 'workplansiq.activationContinuation';
export const SUBSCRIPTION_PENDING = 'SUBSCRIPTION_PENDING';
export const ACTIVATED = 'ACTIVATED';

// Until the backend exposes onboarding/membership decisions, authenticated
// accounts without memberships use the Admin Subscription mock flow.
export const isMockSubscriptionOnboardingEnabled = () =>
  process.env.REACT_APP_USE_MOCK_SUBSCRIPTION_ONBOARDING !== 'false';

export const readPendingOnboarding = () => {
  try {
    const value = localStorage.getItem(PENDING_ACTIVATION_KEY)
      || sessionStorage.getItem(PENDING_ACTIVATION_KEY);
    return value ? JSON.parse(value) : null;
  } catch { return null; }
};

export const savePendingOnboarding = (value) => {
  const serialized = JSON.stringify(value);
  localStorage.setItem(PENDING_ACTIVATION_KEY, serialized);
  sessionStorage.setItem(PENDING_ACTIVATION_KEY, serialized);
};

export const markActivationContinuation = (email) => {
  localStorage.setItem(ACTIVATION_CONTINUATION_KEY, JSON.stringify({
    email: email?.trim().toLowerCase() || null,
    createdAt: new Date().toISOString(),
  }));
};

export const hasActivationContinuation = (signedInEmail) => {
  try {
    const continuation = JSON.parse(localStorage.getItem(ACTIVATION_CONTINUATION_KEY));
    const activationEmail = continuation?.email?.trim().toLowerCase();
    const accountEmail = signedInEmail?.trim().toLowerCase();

    return Boolean(continuation && (!activationEmail || !accountEmail || activationEmail === accountEmail));
  } catch {
    return false;
  }
};

export const clearActivationContinuation = () => {
  localStorage.removeItem(ACTIVATION_CONTINUATION_KEY);
};

export const savePricingIntentFromRedirect = (redirectTo, signedInEmail) => {
  try {
    if (!redirectTo) return false;
    const url = new URL(redirectTo, window.location.origin);
    if (url.pathname !== '/onboarding') return false;

    const planCode = url.searchParams.get('planCode') || url.searchParams.get('plan');
    const capacity = Number(url.searchParams.get('capacity'));
    const durationMonths = Number(url.searchParams.get('durationMonths') || url.searchParams.get('duration'));
    const locale = url.searchParams.get('locale') || 'en';
    const planNames = {
      'wla-sprint': 'WLA Diagnostic Sprint',
      'workforce-intelligence': 'Continuous Workforce Intelligence',
    };

    if (!planNames[planCode]) return false;
    if (!Number.isSafeInteger(capacity) || capacity < 100) return false;
    if (!Number.isSafeInteger(durationMonths) || durationMonths < 1) return false;

    savePendingOnboarding({
      ...(readPendingOnboarding() || {}),
      email: signedInEmail?.trim().toLowerCase() || null,
      planCode,
      planTitle: planNames[planCode],
      capacity,
      durationMonths,
      locale: ['en', 'id'].includes(locale) ? locale : 'en',
      status: ACTIVATED,
      onboardingStatus: SUBSCRIPTION_PENDING,
      selectionSource: 'pricing',
      selectedAt: new Date().toISOString(),
    });
    return true;
  } catch {
    return false;
  }
};

export const hasPendingOnboarding = (signedInEmail) => {
  const draft = readPendingOnboarding();
  const hasSelectedPlan = Boolean(draft?.planCode || draft?.planTitle);
  const draftEmail = draft?.email?.trim().toLowerCase();
  const accountEmail = signedInEmail?.trim().toLowerCase();
  const belongsToSignedInUser = !draftEmail || !accountEmail || draftEmail === accountEmail;

  return draft?.status === ACTIVATED
    && draft?.onboardingStatus === SUBSCRIPTION_PENDING
    && hasSelectedPlan
    && belongsToSignedInUser;
};

// Temporary recovery while onboarding and membership APIs are not available.
// An activated administrator with a retained plan may resume even when an older
// mock run left the onboarding status out of sync with its workspace record.
export const hasRecoverableAdminOnboarding = (signedInEmail) => {
  const draft = readPendingOnboarding();
  const hasSelectedPlan = Boolean(draft?.planCode || draft?.planTitle);
  const draftEmail = draft?.email?.trim().toLowerCase();
  const accountEmail = signedInEmail?.trim().toLowerCase();
  const belongsToSignedInUser = Boolean(
    draftEmail && accountEmail && draftEmail === accountEmail,
  );

  return draft?.status === ACTIVATED
    && hasSelectedPlan
    && belongsToSignedInUser;
};
